import React, {useCallback, useEffect, useState} from 'react';
import {useRecoilState} from 'recoil';
import {loadingAtom} from '../../atoms';
import {getRentalContracts, deleteRentalContract} from '../../utils/requestUtils';
import {getAuthHeader, getAuthToken} from '../../utils/auth';
import {showConfirm} from '../../core/dialog';
import Config from '../../core/config';
import './CompanyClosedDaysCalendar.css';

const apiUrl = Config.apiUrl;

const RentalContractsConfiguration = ({disabled = false}) => {
    const [, setLoading] = useRecoilState(loadingAtom);
    const [contracts, setContracts] = useState([]);
    const [name, setName] = useState('');
    const [file, setFile] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [uploading, setUploading] = useState(false);

    const resetFeedback = () => {
        setErrorMessage('');
        setSuccessMessage('');
    };

    const loadContracts = useCallback(async () => {
        resetFeedback();
        setLoading(true);
        try {
            const response = await getRentalContracts();
            setContracts(response || []);
        } catch (error) {
            console.error('Erro ao carregar contratos de locação:', error);
            setErrorMessage('Não foi possível carregar os contratos.');
        } finally {
            setLoading(false);
        }
    }, [setLoading]);

    useEffect(() => {
        loadContracts();
    }, [loadContracts]);

    const handleFileChange = (e) => {
        resetFeedback();
        setFile(e.target.files[0] || null);
    };

    const handleUpload = async () => {
        if (disabled || uploading) return;
        resetFeedback();
        if (!name.trim()) {
            setErrorMessage('Informe um nome para o contrato.');
            return;
        }
        if (!file) {
            setErrorMessage('Selecione um arquivo PDF.');
            return;
        }
        setUploading(true);
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('name', name.trim());
            fd.append('file', file);
            const res = await fetch(`${apiUrl}/api/rentalcontracts`, {
                method: 'POST',
                headers: {...getAuthHeader()},
                body: fd,
            });
            if (res.ok) {
                setSuccessMessage('Contrato anexado com sucesso.');
                setName('');
                setFile(null);
                const fileInput = document.getElementById('rental-contract-file-input');
                if (fileInput) fileInput.value = '';
                await loadContracts();
            } else {
                let msg = `Erro ${res.status}`;
                try {
                    const body = await res.json();
                    msg = body.erro || body.message || msg;
                } catch {}
                setErrorMessage(msg);
            }
        } catch (error) {
            setErrorMessage('Erro de conexão: ' + error.message);
        } finally {
            setUploading(false);
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (disabled) return;
        const confirmed = await showConfirm('Remover este contrato?');
        if (!confirmed) return;
        resetFeedback();
        setLoading(true);
        try {
            await deleteRentalContract(id);
            setSuccessMessage('Contrato removido com sucesso.');
            setContracts((prev) => prev.filter((c) => c.id !== id));
        } catch (error) {
            console.error('Erro ao remover contrato de locação:', error);
            let msg = 'Não foi possível remover o contrato.';
            try {
                const body = JSON.parse(error.message);
                msg = body.erro || body.message || msg;
            } catch {}
            setErrorMessage(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="company-closed-days-calendar" style={{maxWidth: '700px'}}>
            <div className="header-label">Contratos de locação</div>

            <div style={{display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '420px', margin: '10px auto'}}>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label>Nome do contrato:</label>
                    <input
                        type="text"
                        placeholder="Ex: Contrato padrão"
                        value={name}
                        onChange={(e) => { resetFeedback(); setName(e.target.value); }}
                        disabled={disabled || uploading}
                        style={{width: '100%', boxSizing: 'border-box'}}
                    />
                </div>

                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label>Arquivo PDF:</label>
                    <input
                        id="rental-contract-file-input"
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={handleFileChange}
                        disabled={disabled || uploading}
                    />
                </div>
            </div>

            <div className="calendar-actions">
                <button onClick={handleUpload} disabled={disabled || uploading}>
                    Anexar contrato
                </button>
            </div>

            {successMessage && (
                <div className="calendar-status calendar-status-success">{successMessage}</div>
            )}
            {errorMessage && (
                <div className="calendar-status calendar-status-error">{errorMessage}</div>
            )}

            <table style={{width: '100%', maxWidth: '600px', margin: '20px auto', borderCollapse: 'collapse'}}>
                <thead>
                    <tr>
                        <th style={{textAlign: 'left', borderBottom: '1px solid #ccc', padding: '6px'}}>Nome</th>
                        <th style={{borderBottom: '1px solid #ccc', padding: '6px'}} />
                    </tr>
                </thead>
                <tbody>
                    {contracts.length === 0 && (
                        <tr>
                            <td colSpan={2} style={{padding: '10px', textAlign: 'center', color: '#777'}}>
                                Nenhum contrato anexado.
                            </td>
                        </tr>
                    )}
                    {contracts.map((c) => (
                        <tr key={c.id}>
                            <td style={{padding: '6px', borderBottom: '1px solid #eee'}}>{c.name}</td>
                            <td style={{padding: '6px', borderBottom: '1px solid #eee', textAlign: 'right', whiteSpace: 'nowrap'}}>
                                <a
                                    href={`${apiUrl}/api/rentalcontracts/${c.id}/file?auth=${encodeURIComponent(getAuthToken())}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{marginRight: '10px'}}
                                >
                                    Visualizar
                                </a>
                                <button onClick={() => handleDelete(c.id)} disabled={disabled}>
                                    Remover
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default RentalContractsConfiguration;
